import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { OAuthService } from 'angular-oauth2-oidc';
import * as _ from 'lodash';

import { AclService } from '@aes/acl/acl.service';
import { PortalAuthority } from '@appshared';

export interface ITicketSseEmitter {
  toBeReconciled?: number
  txNotFound?: number
  orderNotFound?: number
}

/**
 * Sale Event Source Service
 */
@Injectable({
  providedIn: 'root',
})
export class SaleSseService {
  private _ticketSseInfoState = new ReplaySubject<ITicketSseEmitter | null>(1);

  private _ticketSseInfoStateCache: ITicketSseEmitter = null;

  private _eventSources: object = {};

  protected SUMMARY_SSE_TYPE = 'TICKET_SUMMARY_SSE';

  constructor(
    public _http: HttpClient,
    private _zone: NgZone,
    private _oauthSrv: OAuthService,
    private _aclService: AclService,
  ) { }

  /**
   * Listen ticket summary event source.
   *
   * @param {string} type
   * @return {*}  {Observable<ITicketSummaryInfo>}
   * @memberof SaleEventSourceService
   */
  listen(): Observable<ITicketSseEmitter> {
    // Destroy existing events if needed
    this.destroy();

    // return empty observable if not eligible for listen due to has no FUNC/PER
    if (!this.eligibleForListen()) {
      return of({});
    }

    // listen SSE event
    return Observable.create(
      observer => {
        if (this._eventSources[this.SUMMARY_SSE_TYPE]) {
          this._eventSources[this.SUMMARY_SSE_TYPE].listener.close();
        }

        const source = new EventSourcePolyfill(`/sale-data-svc/api/tickets/summary-sse`, {
          headers: {
            'Authorization': 'Bearer ' + this._oauthSrv.getAccessToken()
          }
        });

        this._eventSources[this.SUMMARY_SSE_TYPE] = {
          listener: source
        };

        source.onmessage = event => {
          if (!_.isEmpty(event?.data) && !_.isEqual(this._ticketSseInfoStateCache, event?.data)) {
            this._ticketSseInfoStateCache = JSON.parse(event.data);
            this._ticketSseInfoState.next(this._ticketSseInfoStateCache);
            this._zone.run(() => {
              observer.next(this._ticketSseInfoStateCache);
            });
          }
        };

        source.onerror = event => {
          this._ticketSseInfoState.next(null);
          this._zone.run(() => {
            observer.error(event);
          });
        };

      }
    ).subscribe();
  }

  /**
   * close connection
   */
  destroy(): void {
    Object.keys(this._eventSources).forEach((key: string) => {
      const eventSource = this._eventSources[key];
      if (eventSource && eventSource.listener) {
        eventSource.listener.close();
      }
    });
  }

  getTicketSseInfoState(): Observable<ITicketSseEmitter | null> {
    return this._ticketSseInfoState.asObservable();
  }

  private eligibleForListen(): boolean {
    return this._aclService.hasAnyFunction([PortalAuthority.FUNC__FUNC_VIEW__SALE_DATA]);
  }
}
